// 1. React
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";

// 2. Helpers
import { transformRoute, useTranslate } from "helpers";

// 3. Types
import { VehicleOfferRentCardProps, VehicleOfferRentRef } from "./types";
import { FleetVehicleOfferRentRow } from "services/api/fleet/vehicles/offers/types";

// 4. API & Data
import {
  deleteFleetVehicleOfferRentAPI,
  getFleetVehicleOfferRentsAPI,
} from "services/api/fleet/vehicles/offers/api";
import { DataWithPagination } from "services/api";
import { initialOfferRentDataWithPagination } from "./consts";
import { Pagination } from "services/search";

// 5. Navigation
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";

// 6. UI Components
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { OfferRentStatusDropdown } from "ui/components/Dropdown/OfferRentStatusDropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// 7. Local Components
import VehicleOfferRentEditCard from "../../../VehicleOfferRentEditCard/VehicleOfferRentEditCard";
import VehicleContractRentEditCard from "../../../VehicleContractRentEditCard/VehicleContractRentEditCard";
import VehicleInvoiceRentEditCard from "../../../VehicleInvoiceRentEditCard/VehicleInvoiceRentEditCard";
import VehicleOfferRentSearchResults from "../VehicleOfferRentSearchResults/VehicleOfferRentSearchResults";


const VehicleOfferRentCard = forwardRef<VehicleOfferRentRef, VehicleOfferRentCardProps>((props, ref) => {
  const { fleetVehicleDetail, onContractCreated, onInvoiceCreated } = props;

  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleOfferRentRow>
  >(initialOfferRentDataWithPagination);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [offerId, setOfferId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showContractModal, setShowContractModal] = useState<boolean>(false);
  const [contractOfferRentId, setContractOfferRentId] = useState<string>("");
  const [showInvoiceModal, setShowInvoiceModal] = useState<boolean>(false);
  const [invoiceContractRentId, setInvoiceContractRentId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id, statusFilter);
  }, [fleetVehicleDetail.id, statusFilter]);

  const fetchSearchResult = async (pagination: Pagination, vehicleId: string, status?: string | null) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleOfferRentsAPI(pagination, { vehicleId, offerRentStatusId: status });

      setSearchResults(results);
    } catch {
      setSearchResults(initialOfferRentDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleStatusFilterChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, _name: string, item: DropdownOption) => {
      setStatusFilter(item.value);
      if (fleetVehicleDetail.id)
        fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id, item.value);
    },
    []
  );

  const handleRowClick = useCallback(async (id: string) => {
    setOfferId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setOfferId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(newPagination, fleetVehicleDetail.id, statusFilter);
  };

  const handleEditSuccess = useCallback(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id, statusFilter);
    setShowAddNewModal(false);
  }, [fleetVehicleDetail.id, searchResults.pagination, statusFilter]);


  const handleOfferClose = () => {
    setShowAddNewModal(false);
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id, statusFilter);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setOfferId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!offerId || !fleetVehicleDetail.id) return;

    try {
      const deleted = await deleteFleetVehicleOfferRentAPI(
        fleetVehicleDetail?.id,
        offerId,
        t
      );

      if (deleted && fleetVehicleDetail.id) {
        await fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id, statusFilter);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setOfferId("");
    } catch (err) {
      console.error("Greška prilikom brisanja ponude:", err);
    }
  }, [offerId, t, fleetVehicleDetail.id, searchResults.pagination, statusFilter]);

  const handleDeleteDialogClose = useCallback(() => {
    setOfferId("");
    setOpenDialog(false);
  }, []);

  const openAddNew = () => {
    setOfferId("");
    setShowAddNewModal(true);
  };

  const handleCreateContract = (offerRentId: string) => {
    setShowAddNewModal(false);
    setContractOfferRentId(offerRentId);
    setShowContractModal(true);
  };

  const handleContractClose = () => {
    setShowContractModal(false);
    setContractOfferRentId("");
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id, statusFilter);
    if (onContractCreated) onContractCreated();
  };

  const handleCreateInvoice = (contractRentId: string) => {
    setShowContractModal(false);
    setInvoiceContractRentId(contractRentId);
    setShowInvoiceModal(true);
  };

  const handleInvoiceClose = () => {
    setShowInvoiceModal(false);
    setInvoiceContractRentId("");
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id, statusFilter);
    if (onContractCreated) onContractCreated();
    if (onInvoiceCreated) onInvoiceCreated();
  };

  useImperativeHandle(ref, () => ({
    openAddNew,
  }));

  const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.preventDefault();
    event.stopPropagation();

    navigate(
      transformRoute(
        ContactRoutes.ContactDetailPageRouter,
        {
          id: id,
        }
      )
    );
  }

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="space-y-3 mt-1 ">
        <div className="flex justify-between items-center">
          <div className="px-3">
            <OfferRentStatusDropdown
              name="statusFilter"
              placeholder={t("admin.fleet.vehicle.offer.rent.search.status.placeholder")}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              width="small"
              height="small"
            />
          </div>
          <div className="flex items-center gap-2 px-3 my-3 ">
            <Button
              size="small"
              variant="primary"
              children={t("admin.fleet.vehicle.offer.rent.search.add")}
              onClick={handleAddNew}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
        <div className="m-[-1px]">
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.vehicle.offer.search.noresults")} />
          ) : (
            <VehicleOfferRentSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onContactClick={handleContactClick}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <VehicleOfferRentEditCard
        show={showAddNewModal}
        vehicleId={fleetVehicleDetail.id}
        offerId={offerId}
        onSuccess={handleEditSuccess}
        onClose={handleOfferClose}
        onCreateContract={handleCreateContract}
      />
      <VehicleContractRentEditCard
        show={showContractModal}
        vehicleId={fleetVehicleDetail.id!}
        contractId=""
        offerRentId={contractOfferRentId}
        onSuccess={handleContractClose}
        onClose={handleContractClose}
        onCreateInvoice={handleCreateInvoice}
      />
      <VehicleInvoiceRentEditCard
        show={showInvoiceModal}
        vehicleId={fleetVehicleDetail.id!}
        contractRentId={invoiceContractRentId}
        onSuccess={handleInvoiceClose}
        onClose={handleInvoiceClose}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.offer.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.offer.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.offer.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.offer.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
});

export default memo(VehicleOfferRentCard);
